<template>
  <div class="p-6 dark:text-gray-50" v-if="(canAddLoadshedding || user?.is_staff)">
    <div class="mb-6 flex space-x-2 items-center">
      <h1 class="text-2xl font-semibold dark:text-white">Add New Loadshedding {{ isMultiSubmit ? '(Optional)' : ''}}</h1>
      <div class="relative group">
      <button
        type="button"
        class="bg-transparent hover:bg-transparent"
        @click="startTutorial"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
      </button>
        <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </div>


    <form @submit.prevent="submitForm" class="space-y-4 p-6 glass-card">
      <div
        v-for="field in loadsheddingFields"
        :key="field.key"
        class="flex flex-col"
      >
        <label :for="field.key" class="mb-1 font-medium text-gray-700 dark:text-white">{{
          field.label
        }}</label>
        <input
          v-if="field.type !== 'searchableSelect'"
          :id="field.key"
          v-model="loadsheddingData[field.key]"
          :type="field.type"
          :required="field.required"
          :class="[ 'w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2', fieldErrors[field.key]
                ? 'ring-2 ring-red-500  focus:ring-red-500'
                : 'focus:ring-blue-500'
              ]"
        />
        <div v-else class="relative">
          <input 
            :id="field.key"
            v-model="siteSearch"
            :required="field.required"
            :placeholder="getSelectedSiteName || 'Search Site...'"
            :class="[ 'w-full', fieldErrors[field.key]
                ? 'border border-red-300 rounded px-3 py-2 ring-2 ring-red-400 focus:outline-none focus:ring-2 focus:ring-red-400 mb-3'
                : 'border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3'
            ]"
            @focus="handleSiteFocus"
            @blur="handleSiteBlur"
            :disabled="isMultiSubmit" 
          >
          <div
            v-show="isSiteDropdownVisible" 
            class="absolute w-full mt-1 max-h-60 overflow-y-auto bg-white border border-gray-300 rounded shadow-lg z-10"
            >
            <div 
              v-for="site in filteredSites"
              :key="site.id"
              class="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:bg-gray-600 dark:text-gray-50 dark:hover:text-gray-900"
              @mousedown="selectSite(site)"
            >
              {{ site.name }}
            </div>
            <div 
              v-if="filteredSites.length === 0" 
              class="px-3 py-2 text-gray-500"
            >
              No Sites found
            </div>
          </div>
        </div>
        
        <FieldError :message="fieldErrors[field.key]"/>
      </div>
      <div class="flex justify-end space-x-2">
        <button v-if="!isMultiSubmit"
          type="button"
          @click="$router.push({ name: 'Loadsheddings' })"
          class="btn bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Cancel
        </button>
        <button v-if="isMultiSubmit"
          type="button"
          @click="skipMultiSubmit"
          class="btn bg-gray-300 text-gray-700 hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          Skip
        </button>
        <button
          id="submit-loadshedding-btn"
          type="submit"
          class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Loadshedding
        </button>
      </div>
    </form>
  </div>

  <!-- Error Message -->
  <PageError v-if="permsLoaded && !canAddLoadshedding && !user?.is_staff"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />
</template>

<script>
import { createLoadshedding } from "@/services/loadshedding-service";
import { getSites } from "@/services/siteService";
import { getAllOrgs } from "@/services/orgService";
import FieldError from "@/components/FieldError.vue";
import { useStore } from "@/store";
import PageError from "@/components/PageError.vue";
import { errorMessages } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useTutorial } from "@/composables/tutorial";

export default {
  emits: ['loadshedding-submitted', 'loadshedding-skipped'],
  props: {
    isMultiSubmit: {
      type: Boolean,
      default: false
    },
    newmultiSubmitSite: {
      type: Number
    },
    submittedSite: {
      type: Object,
      default: null
    }
  },
  components: {
    FieldError,
    PageError,
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      errorMessages: errorMessages,
      addLoadsheddingTutorialSteps: [
        {
          element: '#area_id',
          action: 'type',
          value: 'AREA-001',
          message: 'Enter the area ID for this loadshedding area.'
        },
        {
          element: '#site',
          action: 'display',
          message: 'Search and select the site this loadshedding area belongs to.'
        },
        {
          element: '#submit-loadshedding-btn',
          action: 'display',
          message: 'Click to create the loadshedding area.'
        },
      ],
      multiSubmitSite: "",
      fieldErrors: {},
      siteSearch: "",
      isSiteDropdownVisible: false,
      loadsheddingData: {
        name: "",
        area_id: "",
        org: null,
        site: null,
      },
      sites: [],
      organizations: {},
      canAddLoadshedding: false,
      loadsheddingFields: [
        { key: "area_id", label: "Area ID", type: "text", required: true },
        {
          key: "site",
          label: "Site",
          type: "searchableSelect",
          required: true,
          options: {},
        },
      ],
    };
  },
  async mounted() {
    const validation = this.tenant.validateSchemaForOperation();
    await Promise.all([
      this.tenant.fetchOrganizations(),
      this.fetchSites(validation.schemaName)
    ])
  },
  watch: {
    user: {
      handler(user) {
        if (user) {
          this.checkPermissions();
          const validation = this.tenant.validateSchemaForOperation();
          this.fetchSites(validation.schemaName);
          this.updateFieldOptions();;
        }
      },
      immediate: true
    },
  },
  methods: {
    handleShowHowToLoadShedding(value){
      this.showHowToLoadShedding = value;
    },
    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },
    validateFields(fields, fieldsToCheck) {
      const errors = {};
      for (const fieldName of fieldsToCheck) {
        const value = fields[fieldName];
        if (!value || (typeof value === "string" && !value.trim()) || (value instanceof Array && value.length === 0)) {
          errors[fieldName] = "This field cannot be empty";
        }
      }
      return errors;
    },
    async startTutorial() {
      const originalData = { ...this.loadsheddingData };
      await this._start(this.addLoadsheddingTutorialSteps, () => {
        this.loadsheddingData = { ...originalData };
      });
    },
    async checkPermissions() {
      if (this.user) {
        this.canAddLoadshedding = await useStore.hasPermission("Can add loadshedding area");
      }
    },
    async fetchSites(schemaName=null) {
      try {
        const sites = await getSites(schemaName);
        this.sites = sites
        if (this.isMultiSubmit){
          this.multiSubmitSite =this.sites.find(item => item.id == this.newmultiSubmitSite);
          this.selectSite(this.multiSubmitSite); 
        }
      } catch (error) {
        console.error("Error fetching sites:", error);
      }
    },
    updateFieldOptions() {
      this.loadsheddingFields.find((field) => field.key === "site").options =
        this.sites;
    },
    async submitForm() {
      try {
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.error = validation.error;
          this.errorCode = "Failed to create Loadshedding Area!";
          return;
        }
        // Generate the name before submission
        const selectedSite = this.sites[this.loadsheddingData.site];
        const selectedOrg = this.organizations[this.loadsheddingData.org];
        this.loadsheddingData.name = `${selectedOrg}-${selectedSite}-${this.loadsheddingData.area_id}`;

        const newLoadshedding = await createLoadshedding(this.loadsheddingData, validation.schemaName);

        if (this.isMultiSubmit){
          this.$emit('loadshedding-submitted', this.loadsheddingData)
        } else {
          this.$router.push({ name: "Loadsheddings", query: this.tenant.addSchemaParam() });
        }

      } catch (error) {
        this.fieldErrors = {}; 
        const items = this.loadsheddingFields;
        const errData = error?.response?.data;

        items.forEach(element => {
          const fieldError = errData?.[element.key];
          if (fieldError?.[0]) {
            this.fieldErrors[element.key] = fieldError[0]; 
          }
        });
        console.error("Error creating loadshedding:", error);
        
      }
    },
    handleSiteFocus() {
      this.isSiteDropdownVisible = true;
      this.siteSearch = "";
    },
    handleSiteBlur() {
      setTimeout(() => {
        this.isSiteDropdownVisible = false;
      }, 200);
    },
    selectSite(site) {
      this.loadsheddingData.site = site.id;
      this.siteSearch = site.name;
      this.isSiteDropdownVisible = false;
      
    },
    skipMultiSubmit(){
      this.$emit('loadshedding-skipped','skipped')
    }
  },
  computed:{
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    filteredSites() {
      if (!this.siteSearch) return this.sites;
      return this.sites.filter((site) =>
        site.name.toLowerCase().includes(this.siteSearch.toLowerCase()) ||
        site.id.toString().includes(this.siteSearch)
      );
    },
    getSelectedSiteName() {
      if (!this.loadsheddingData.site) return null;
      const selectedSite = this.sites.find(p => p.id === this.loadsheddingData.site);
      return selectedSite? `${selectedSite.name}` : null;
    },
    user(){
      return useStore.getUser();
    },
  }
};
</script>
